import { scrollToSelector } from '../helpers/scroll';
import { SITE_NAVBAR_COMPACT_SETTLED_EVENT } from './site-navbar';

/**
 * site-navigation — víceúrovňové menu (vanilla TS, bez závislosti na Bootstrapu)
 * -----------------------------------------------------------------------------
 * Komponenta je nezávislá na kontejneru: korenovým elementem je samotný seznam
 * (`ul`), takže stejné menu jde použít v liště (site-navbar), v postranním
 * panelu i v patičce, a to vícekrát na jedné stránce.
 *
 * Vlastnosti:
 *  - neomezený počet úrovní (L1 v řádku / v akordeonu, L2 dolů, L3+ do strany)
 *  - rozbalování klikem na samostatný control (odkaz zůstává funkční),
 *    na desktopu volitelně i hoverem, ale jen u první úrovně
 *  - panely jsou pozicované do viewportu (nikdy nepřetečou okno ani neroztáhnou stránku)
 *  - položky, které se nevejdou do řádku, se přesouvají do „more“ panelu (…);
 *    v liště se „more“ neměří během `.site-navbar--compacting`, až po
 *    `site-navbar:compact-settled` (bez probliknutí uprostřed animace)
 *  - pod breakpointem akordeon (panely zůstávají v toku)
 *
 * Veškeré chování stojí na delegovaných listenerech na korenovém elementu, takže
 * přesouvání položek mezi řádkem a „more“ panelem nic nerozbije.
 *
 * HTML kontrakt:
 *   [data-site-navigation]               korenový seznam (ul)
 *   [data-site-navigation-item]          položka (li)
 *   [data-site-navigation-toggle]        tlačítko rozbalení
 *   [data-site-navigation-submenu]       vnořený seznam (ul)
 *   [data-site-navigation-overflow]      položka „more“ (volitelná, jen vodorovné menu)
 *   [data-site-navigation-overflow-list] seznam v položce „more“
 *   [data-site-navigation-keep-open]     odkaz, který menu nezavírá
 *   [data-site-navigation-breakpoint]    [px] hranice akordeon / panely (default 768)
 *   [data-site-navigation-flyout]        "side" | "down" — směr vnořených panelů (default side)
 */

export interface SiteNavigationOptions {
    /** korenový element nebo selektor (default: [data-site-navigation]) */
    root?: HTMLElement | string;
    /** [px] pod touto šířkou okna se používá akordeon */
    breakpoint?: number;
    /** rozbalovat první úroveň i hoverem (desktop) */
    hover?: boolean;
    /** [ms] zpoždění zavření po opuštění menu kurzorem */
    hoverCloseDelay?: number;
    /** [px] minimální odstup panelu od hrany okna */
    viewportPadding?: number;
    /** [px] odstup panelu L2 od položky první úrovně */
    dropdownOffset?: number;
    /** [px] odstup vnořeného panelu od rodičovského */
    flyoutOffset?: number;
    /** směr vnořených panelů L3+ (side = do strany, down = pod položkou) */
    flyout?: 'side' | 'down';
}

const SELECTOR = {
    root: '[data-site-navigation]',
    item: '[data-site-navigation-item]',
    toggle: '[data-site-navigation-toggle]',
    submenu: '[data-site-navigation-submenu]',
    overflowItem: '[data-site-navigation-overflow]',
    overflowList: '[data-site-navigation-overflow-list]',
    keepOpen: '[data-site-navigation-keep-open]',
};

const CLASS = {
    open: 'is-open',
    ready: 'site-navigation--ready',
    hoverEnabled: 'site-navigation--hover-enabled',
    flipX: 'site-navigation__item--flip-x',
    inOverflow: 'site-navigation__item--in-overflow',
};

const DEFAULTS = {
    breakpoint: 768,
    hover: true,
    hoverCloseDelay: 250,
    viewportPadding: 12,
    dropdownOffset: 4,
    flyoutOffset: 2,
};

const MEASURE_DEBOUNCE = 100;
const NAVBAR_COMPACTING_CLASS = 'site-navbar--compacting';
/** pod touto šířkou slotu ještě není layout hotový — neměnit „more“ (jinak všechno spadne do more a problikne) */
const MIN_AVAILABLE_WIDTH = 48;

const numberFromData = (root: HTMLElement, name: string, fallback: number): number => {
    const raw = root.getAttribute(name);
    if (!raw) {
        return fallback;
    }
    const value = parseInt(raw, 10);

    return isNaN(value) ? fallback : value;
};

export class SiteNavigation {
    private readonly root: HTMLElement;
    private readonly overflowItem: HTMLElement | null;
    private readonly overflowList: HTMLElement | null;

    private readonly breakpoint: number;
    private readonly hover: boolean;
    private readonly hoverCloseDelay: number;
    private readonly viewportPadding: number;
    private readonly dropdownOffset: number;
    private readonly flyoutOffset: number;
    private readonly flyoutOpensDown: boolean;

    private hoverTimer = 0;
    private hoverLocked: HTMLElement | null = null;
    private positionFrame = 0;
    private measureTimer = 0;
    private measuring = false;
    private wasMobile: boolean;
    private resizeObserver: ResizeObserver | null = null;
    /** šířky L1 položek v hlavním řádku (po posledním stabilním měření) */
    private readonly itemWidthCache = new WeakMap<HTMLElement, number>();
    private moreWidthCache = 0;

    constructor(root: HTMLElement, options: SiteNavigationOptions = {}) {
        this.root = root;
        this.overflowItem =
            Array.from(root.children).find(
                (child): child is HTMLElement => child instanceof HTMLElement && child.matches(SELECTOR.overflowItem),
            ) ?? null;
        this.overflowList = this.overflowItem?.querySelector<HTMLElement>(SELECTOR.overflowList) ?? null;

        this.breakpoint =
            options.breakpoint ?? numberFromData(root, 'data-site-navigation-breakpoint', DEFAULTS.breakpoint);
        this.hover = options.hover ?? DEFAULTS.hover;
        this.hoverCloseDelay = options.hoverCloseDelay ?? DEFAULTS.hoverCloseDelay;
        this.viewportPadding = options.viewportPadding ?? DEFAULTS.viewportPadding;
        this.dropdownOffset = options.dropdownOffset ?? DEFAULTS.dropdownOffset;
        this.flyoutOffset = options.flyoutOffset ?? DEFAULTS.flyoutOffset;
        this.flyoutOpensDown = options.flyout === 'down' || root.getAttribute('data-site-navigation-flyout') === 'down';
        this.wasMobile = this.isMobile();

        this.bind();
        this.root.classList.add(CLASS.ready);
        this.measure();
    }

    /** Přepočet po vnější změně (např. dynamicky doplněné položky). */
    refresh = (): void => {
        this.measure();
        this.positionOpen();
    };

    destroy = (): void => {
        this.root.removeEventListener('click', this.onClick);
        this.root.removeEventListener('pointerover', this.onPointerOver);
        this.root.removeEventListener('pointerleave', this.onPointerLeave);
        this.root.removeEventListener('keydown', this.onKeyDown);
        this.root.removeEventListener('focusout', this.onFocusOut);
        this.root.removeEventListener('scroll', this.onInnerScroll, true);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener(SITE_NAVBAR_COMPACT_SETTLED_EVENT, this.onNavbarCompactSettled);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
        this.clearHoverTimer();
        window.clearTimeout(this.measureTimer);
        this.root.classList.remove(CLASS.ready);
    };

    private bind(): void {
        this.root.addEventListener('click', this.onClick);
        this.root.addEventListener('pointerover', this.onPointerOver);
        this.root.addEventListener('pointerleave', this.onPointerLeave);
        this.root.addEventListener('keydown', this.onKeyDown);
        this.root.addEventListener('focusout', this.onFocusOut);
        // scroll uvnitr panelu (nebubla) — vnorene panely musi zustat u sve polozky
        this.root.addEventListener('scroll', this.onInnerScroll, true);
        document.addEventListener('click', this.onDocumentClick);
        // během compact animace overflow neměnit — až po compact-settled
        document.addEventListener(SITE_NAVBAR_COMPACT_SETTLED_EVENT, this.onNavbarCompactSettled);
        window.addEventListener('resize', this.onResize);
        window.addEventListener('scroll', this.onScroll, { passive: true });

        // „more“ se počítá z dostupné šířky slotu — ta se mění nezávisle na obsahu menu
        if (typeof ResizeObserver !== 'undefined' && this.overflowItem) {
            this.resizeObserver = new ResizeObserver(() => {
                this.scheduleMeasure();
            });
            this.resizeObserver.observe(this.root.parentElement ?? this.root);
        }
    }

    // -------------------------------------------------------------------------
    // Události
    // -------------------------------------------------------------------------

    private onClick = (event: MouseEvent): void => {
        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const toggle = target.closest<HTMLElement>(SELECTOR.toggle);
        if (toggle && this.root.contains(toggle)) {
            event.preventDefault();
            const item = toggle.closest<HTMLElement>(SELECTOR.item);
            if (item) {
                this.toggleItem(item);
            }

            return;
        }

        const link = target.closest<HTMLAnchorElement>('a[href]');
        if (!link || !this.root.contains(link) || link.matches(SELECTOR.keepOpen)) {
            return;
        }

        // odkaz na kotvu v rámci stejné stránky
        const anchor = link.getAttribute('data-scroll-target');
        if (anchor && document.querySelector(anchor)) {
            event.preventDefault();
            this.closeAll();
            scrollToSelector(anchor);

            return;
        }

        // běžný odkaz — jen uklidit rozbalené úrovně (navigace pokračuje)
        this.closeAll();
    };

    private onDocumentClick = (event: MouseEvent): void => {
        const target = event.target as Node | null;
        if (target && this.root.contains(target)) {
            return;
        }

        this.closeAll();
    };

    private onPointerOver = (event: PointerEvent): void => {
        if (!this.isHoverEnabled() || event.pointerType === 'touch') {
            return;
        }

        const target = event.target as HTMLElement | null;
        const item = target ? target.closest<HTMLElement>(SELECTOR.item) : null;
        this.clearHoverTimer();

        if (!item || !this.root.contains(item)) {
            // kurzor je v menu mimo položku — zavřít se zpožděním (přejezd přes mezeru)
            this.hoverTimer = window.setTimeout(this.closeAll, this.hoverCloseDelay);

            return;
        }

        // hoverem se rozbaluje jen první úroveň; panely L2+ jdou otevřít výhradně klikem,
        // aby se pod kurzorem samy neotevíraly a nezavíraly už rozbalenou větev
        if (!this.isTopLevelItem(item)) {
            return;
        }

        if (this.hoverLocked === item) {
            return;
        }
        this.hoverLocked = null;
        this.openChain(item);
    };

    private onPointerLeave = (): void => {
        if (!this.isHoverEnabled()) {
            return;
        }

        this.clearHoverTimer();
        this.hoverLocked = null;
        this.hoverTimer = window.setTimeout(this.closeAll, this.hoverCloseDelay);
    };

    private onKeyDown = (event: KeyboardEvent): void => {
        const target = event.target as HTMLElement | null;

        if (event.key === 'Escape') {
            const item = target ? target.closest<HTMLElement>(`${SELECTOR.item}.${CLASS.open}`) : null;
            if (item) {
                // kontejner (např. lišta) pak už Escape neobsluhuje
                event.preventDefault();
                this.closeItem(item);
                this.toggleOf(item)?.focus();
            } else {
                this.closeAll();
            }

            return;
        }

        const toggle = target ? target.closest<HTMLElement>(SELECTOR.toggle) : null;
        if (!toggle || !this.root.contains(toggle)) {
            return;
        }

        const item = toggle.closest<HTMLElement>(SELECTOR.item);
        if (!item) {
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.closeSiblings(item);
            this.openItem(item);
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault();
            this.closeItem(item);
        }
    };

    private onFocusOut = (event: FocusEvent): void => {
        const next = event.relatedTarget as Node | null;
        if (!next || this.root.contains(next)) {
            return;
        }

        this.closeAll();
    };

    private onNavbarCompactSettled = (event: Event): void => {
        if (!this.isEventFromOwnNavbar(event)) {
            return;
        }

        // font/logo dojel — zahodit cache (šířky z předchozího stavu) a přeměřit
        this.moreWidthCache = 0;
        this.primaryItemsInOrder().forEach((item) => this.itemWidthCache.delete(item));
        this.measure();
        this.positionOpen();
    };

    private onScroll = (): void => {
        this.schedulePosition();
    };

    private onInnerScroll = (): void => {
        this.schedulePosition();
    };

    private onResize = (): void => {
        this.syncViewport();
        this.scheduleMeasure();
        this.schedulePosition();
    };

    // -------------------------------------------------------------------------
    // Otevírání / zavírání
    // -------------------------------------------------------------------------

    private toggleItem(item: HTMLElement): void {
        if (item.classList.contains(CLASS.open)) {
            this.closeItem(item);
            // zabrání okamžitému znovuotevření hoverem pod kurzorem
            this.hoverLocked = item;

            return;
        }

        this.closeSiblings(item);
        this.openItem(item);
    }

    private openItem(item: HTMLElement): void {
        if (!this.submenuOf(item)) {
            return;
        }

        item.classList.add(CLASS.open);
        this.toggleOf(item)?.setAttribute('aria-expanded', 'true');

        if (!this.isMobile()) {
            this.positionSubmenu(item);
        }
    }

    private closeItem = (item: HTMLElement): void => {
        item.classList.remove(CLASS.open, CLASS.flipX);
        this.toggleOf(item)?.setAttribute('aria-expanded', 'false');

        const submenu = this.submenuOf(item);
        if (submenu) {
            submenu.style.left = '';
            submenu.style.top = '';
            submenu.style.maxHeight = '';
            submenu.querySelectorAll<HTMLElement>(`${SELECTOR.item}.${CLASS.open}`).forEach((child) => {
                this.closeItem(child);
            });
        }
    };

    private closeSiblings(item: HTMLElement): void {
        const parent = item.parentElement;
        if (!parent) {
            return;
        }

        Array.from(parent.children).forEach((sibling) => {
            if (sibling !== item && sibling instanceof HTMLElement && sibling.matches(SELECTOR.item)) {
                this.closeItem(sibling);
            }
        });
    }

    private closeAll = (): void => {
        this.clearHoverTimer();
        const open = Array.from(this.root.querySelectorAll<HTMLElement>(`${SELECTOR.item}.${CLASS.open}`));
        open.reverse().forEach(this.closeItem);
    };

    private openChain(item: HTMLElement): void {
        const chain: Array<HTMLElement> = [];
        let current: HTMLElement | null = item;

        while (current && this.root.contains(current)) {
            chain.unshift(current);
            current = current.parentElement ? current.parentElement.closest<HTMLElement>(SELECTOR.item) : null;
        }

        this.root.querySelectorAll<HTMLElement>(`${SELECTOR.item}.${CLASS.open}`).forEach((open) => {
            if (!chain.includes(open)) {
                this.closeItem(open);
            }
        });

        chain.forEach((node) => {
            if (!node.classList.contains(CLASS.open)) {
                this.openItem(node);
            }
        });
    }

    // -------------------------------------------------------------------------
    // Pozicování panelů ve viewportu
    // -------------------------------------------------------------------------

    private clampDropdownPosition(
        left: number,
        top: number,
        width: number,
        height: number,
        viewportWidth: number,
        viewportHeight: number,
        pad: number,
    ): { left: number; top: number } {
        if (left + width > viewportWidth - pad) {
            left = viewportWidth - pad - width;
        }
        if (left < pad) {
            left = pad;
        }
        if (top + height > viewportHeight - pad) {
            top = Math.max(pad, viewportHeight - pad - height);
        }

        return { left, top };
    }

    private positionSubmenu(item: HTMLElement): void {
        const submenu = this.submenuOf(item);
        if (!submenu) {
            return;
        }

        const pad = this.viewportPadding;
        // clientWidth/Height = layout viewport bez scrollbarů (innerWidth panel často přilepí k hraně)
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;

        // reset před měřením, ať se počítá s přirozenými rozměry
        submenu.style.left = '0px';
        submenu.style.top = '0px';
        submenu.style.maxHeight = '';

        const width = submenu.offsetWidth;
        const height = submenu.offsetHeight;
        const itemRect = item.getBoundingClientRect();
        const parentSubmenu = item.parentElement ? item.parentElement.closest<HTMLElement>(SELECTOR.submenu) : null;

        let left: number;
        let top: number;
        let flipped = false;

        if (!parentSubmenu) {
            // L2 — panel pod položkou první úrovně
            if (this.flyoutOpensDown) {
                // menu u pravého okraje — zarovnat panel k pravé hraně triggeru
                left = itemRect.right - width;
            } else {
                left = itemRect.left;
                // u položek vpravo raději zarovnat k pravé hraně triggeru, ať panel nepřetéká
                if (left + width > viewportWidth - pad) {
                    left = itemRect.right - width;
                }
            }
            top = itemRect.bottom + this.dropdownOffset;

            ({ left, top } = this.clampDropdownPosition(left, top, width, height, viewportWidth, viewportHeight, pad));
        } else if (this.flyoutOpensDown) {
            // L3+ dolů pod položkou (ne do strany). Překrytí L2 řeší CSS (z-index + hustší glass).
            left = itemRect.left;
            top = itemRect.bottom + this.dropdownOffset;

            ({ left, top } = this.clampDropdownPosition(left, top, width, height, viewportWidth, viewportHeight, pad));
        } else {
            // L3+ — panel vedle rodičovského panelu, při nedostatku místa na druhou stranu
            const parentRect = parentSubmenu.getBoundingClientRect();
            left = parentRect.right + this.flyoutOffset;
            top = itemRect.top;

            if (left + width > viewportWidth - pad) {
                const mirrored = parentRect.left - this.flyoutOffset - width;
                if (mirrored >= pad) {
                    left = mirrored;
                    flipped = true;
                } else {
                    left = viewportWidth - pad - width;
                }
            }

            if (top + height > viewportHeight - pad) {
                top = Math.max(pad, viewportHeight - pad - height);
            }
        }

        submenu.style.left = `${Math.round(Math.max(pad, left))}px`;
        submenu.style.top = `${Math.round(top)}px`;
        submenu.style.maxHeight = `${Math.round(Math.max(0, viewportHeight - top - pad))}px`;
        item.classList.toggle(CLASS.flipX, flipped);
    }

    private positionOpen = (): void => {
        if (this.isMobile()) {
            return;
        }

        // v pořadí dokumentu = od nejvyšší úrovně, vnořené panely tak vycházejí ze správné pozice rodiče
        this.root.querySelectorAll<HTMLElement>(`${SELECTOR.item}.${CLASS.open}`).forEach((item) => {
            this.positionSubmenu(item);
        });
    };

    private schedulePosition(): void {
        if (this.positionFrame) {
            return;
        }

        this.positionFrame = window.requestAnimationFrame(() => {
            this.positionFrame = 0;
            this.positionOpen();
        });
    }

    // -------------------------------------------------------------------------
    // „More“ — položky, které se nevejdou do řádku
    // -------------------------------------------------------------------------

    private measure = (): void => {
        if (!this.overflowItem || !this.overflowList || this.measuring) {
            return;
        }

        this.measuring = true;

        if (this.isMobile()) {
            this.applyOverflowSplit(this.primaryItemsInOrder(), Number.POSITIVE_INFINITY);
            this.measuring = false;

            return;
        }

        const items = this.primaryItemsInOrder();
        if (!items.length) {
            this.applyOverflowSplit(items, Number.POSITIVE_INFINITY);
            this.measuring = false;

            return;
        }

        const available = this.availableWidth();
        // layout ještě není hotový (pin/init) — neměnit stav, ať „more“ neproblikne
        if (available < MIN_AVAILABLE_WIDTH) {
            this.measuring = false;

            return;
        }

        const widths = this.resolveItemWidths(items);
        if (!widths) {
            this.measuring = false;

            return;
        }

        const gap = this.listGap();
        const total = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, items.length - 1);

        if (total <= available + 1) {
            this.applyOverflowSplit(items, items.length);
            this.measuring = false;

            return;
        }

        const moreWidth = this.measureMoreWidth();
        const budget = available - (moreWidth + gap);
        let used = 0;
        let firstMoved = items.length;

        for (let i = 0; i < items.length; i++) {
            const next = used + widths[i] + (i > 0 ? gap : 0);
            if (next > budget) {
                firstMoved = i;
                break;
            }
            used = next;
        }

        // aspoň jedna položka musí zůstat vidět mimo „more“
        if (firstMoved === 0 && items.length > 1) {
            firstMoved = 1;
        }

        this.applyOverflowSplit(items, firstMoved);
        this.measuring = false;
    };

    private scheduleMeasure(): void {
        // během compact transition nepřepočítávat — settle to řeší synchronně
        if (this.isNavbarCompacting()) {
            return;
        }

        window.clearTimeout(this.measureTimer);
        this.measureTimer = window.setTimeout(() => {
            if (this.isNavbarCompacting()) {
                return;
            }
            this.measure();
            this.positionOpen();
        }, MEASURE_DEBOUNCE);
    }

    /** true = event přišel z lišty, ve které toto menu leží */
    private isEventFromOwnNavbar(event: Event): boolean {
        const navbar = this.root.closest<HTMLElement>('[data-site-navbar]');
        const target = event.target;
        if (!navbar || !(target instanceof Element)) {
            return false;
        }

        return navbar === target || navbar.contains(target);
    }

    private isNavbarCompacting(): boolean {
        return Boolean(this.root.closest(`.${NAVBAR_COMPACTING_CLASS}`));
    }

    /** Šířka slotu (parent), ne obsah menu — jinak „more“ problikne podle vlastní šířky položek */
    private availableWidth(): number {
        const parent = this.root.parentElement;
        if (parent && parent.clientWidth > 0) {
            return parent.clientWidth;
        }

        return this.root.clientWidth;
    }

    /** L1 položky v původním pořadí (viditelné v řádku + přesunuté do „more“) */
    private primaryItemsInOrder(): Array<HTMLElement> {
        const main = this.mainItems();
        const overflowed = this.overflowList
            ? Array.from(this.overflowList.children).filter(
                  (child): child is HTMLElement => child instanceof HTMLElement && child.matches(SELECTOR.item),
              )
            : [];

        return [...main, ...overflowed];
    }

    /**
     * Šířky jako v hlavním řádku. Položky v „more“ mají jiný styl — bereme cache,
     * případně jednorázový restore jen pro doplnění cache (bez finálního apply).
     */
    private resolveItemWidths(items: Array<HTMLElement>): Array<number> | null {
        const missing = items.filter((item) => item.parentElement !== this.root && !this.itemWidthCache.has(item));

        if (missing.length) {
            this.restoreOverflowItems();
            items.forEach((item) => {
                this.itemWidthCache.set(item, item.offsetWidth);
            });
        } else {
            items.forEach((item) => {
                if (item.parentElement === this.root) {
                    this.itemWidthCache.set(item, item.offsetWidth);
                }
            });
        }

        const widths = items.map((item) => this.itemWidthCache.get(item) ?? 0);
        if (widths.some((width) => width <= 0)) {
            return null;
        }

        return widths;
    }

    /**
     * Šířka „more“ bez layout flash: měří se mimo tok (visibility:hidden),
     * stav `hidden` na položce se nemění kvůli měření.
     */
    private measureMoreWidth(): number {
        if (!this.overflowItem) {
            return this.moreWidthCache;
        }

        if (!this.overflowItem.hidden && this.overflowItem.offsetWidth > 0) {
            this.moreWidthCache = this.overflowItem.offsetWidth;

            return this.moreWidthCache;
        }

        if (this.moreWidthCache > 0) {
            return this.moreWidthCache;
        }

        const el = this.overflowItem;
        const style = el.style;
        const prev = {
            hidden: el.hidden,
            position: style.position,
            visibility: style.visibility,
            pointerEvents: style.pointerEvents,
            left: style.left,
            top: style.top,
        };

        el.hidden = false;
        style.position = 'absolute';
        style.visibility = 'hidden';
        style.pointerEvents = 'none';
        style.left = '0';
        style.top = '0';

        const width = el.offsetWidth;

        el.hidden = prev.hidden;
        style.position = prev.position;
        style.visibility = prev.visibility;
        style.pointerEvents = prev.pointerEvents;
        style.left = prev.left;
        style.top = prev.top;

        if (width > 0) {
            this.moreWidthCache = width;
        }

        return this.moreWidthCache || width;
    }

    /**
     * Nastaví overflow jen když se cíl liší od stavu — žádný zbytečný restore/problik.
     * firstMoved >= items.length (nebo Infinity) = „more“ schovat, vše v řádku.
     */
    private applyOverflowSplit(items: Array<HTMLElement>, firstMoved: number): void {
        if (!this.overflowItem || !this.overflowList) {
            return;
        }

        const overflowList = this.overflowList;
        const split = Math.min(Math.max(0, firstMoved), items.length);
        const currentSplit = items.findIndex((item) => overflowList.contains(item));
        const effectiveCurrent = currentSplit === -1 ? items.length : currentSplit;
        const moreShouldShow = split < items.length;

        if (effectiveCurrent === split && this.overflowItem.hidden === !moreShouldShow) {
            return;
        }

        // vrať jen položky, které už v more být nemají
        for (let i = 0; i < split; i++) {
            const item = items[i];
            if (!overflowList.contains(item)) {
                continue;
            }
            this.closeItem(item);
            item.classList.remove(CLASS.inOverflow);
            this.root.insertBefore(item, this.overflowItem);
        }

        if (!moreShouldShow) {
            this.overflowItem.hidden = true;
            // případný zbytek v seznamu (obrana)
            this.restoreOverflowItems();

            return;
        }

        this.overflowItem.hidden = false;

        for (let i = split; i < items.length; i++) {
            const item = items[i];
            if (this.overflowList.contains(item)) {
                continue;
            }
            this.closeItem(item);
            item.classList.add(CLASS.inOverflow);
            this.overflowList.appendChild(item);
        }
    }

    private restoreOverflowItems(): void {
        if (!this.overflowList || !this.overflowItem) {
            return;
        }

        Array.from(this.overflowList.children).forEach((child) => {
            if (!(child instanceof HTMLElement)) {
                return;
            }

            this.closeItem(child);
            child.classList.remove(CLASS.inOverflow);
            this.root.insertBefore(child, this.overflowItem);
        });
    }

    private mainItems(): Array<HTMLElement> {
        return Array.from(this.root.children).filter(
            (child): child is HTMLElement =>
                child instanceof HTMLElement && child.matches(SELECTOR.item) && child !== this.overflowItem,
        );
    }

    private listGap(): number {
        const gap = parseFloat(window.getComputedStyle(this.root).columnGap);

        return isNaN(gap) ? 0 : gap;
    }

    // -------------------------------------------------------------------------
    // Pomocné
    // -------------------------------------------------------------------------

    private submenuOf(item: HTMLElement): HTMLElement | null {
        return (
            Array.from(item.children).find(
                (child): child is HTMLElement => child instanceof HTMLElement && child.matches(SELECTOR.submenu),
            ) ?? null
        );
    }

    private toggleOf(item: HTMLElement): HTMLElement | null {
        return (
            Array.from(item.children).find(
                (child): child is HTMLElement => child instanceof HTMLElement && child.matches(SELECTOR.toggle),
            ) ?? null
        );
    }

    private isMobile(): boolean {
        return window.innerWidth < this.breakpoint;
    }

    /** položka první úrovně — položky v panelech i v „more“ mají jiného rodiče */
    private isTopLevelItem(item: HTMLElement): boolean {
        return item.parentElement === this.root;
    }

    private isHoverEnabled(): boolean {
        return (
            this.hover &&
            !this.isMobile() &&
            this.root.classList.contains(CLASS.hoverEnabled) &&
            window.matchMedia('(hover: hover)').matches
        );
    }

    private syncViewport(): void {
        const mobile = this.isMobile();
        if (mobile === this.wasMobile) {
            return;
        }

        this.wasMobile = mobile;
        this.closeAll();
    }

    private clearHoverTimer(): void {
        if (this.hoverTimer) {
            window.clearTimeout(this.hoverTimer);
            this.hoverTimer = 0;
        }
    }
}

/** Inicializace všech menu na stránce; vrací instance (může jich být víc). */
export const initSiteNavigations = (options: SiteNavigationOptions = {}): Array<SiteNavigation> => {
    const roots =
        typeof options.root === 'string'
            ? Array.from(document.querySelectorAll<HTMLElement>(options.root))
            : options.root
              ? [options.root]
              : Array.from(document.querySelectorAll<HTMLElement>(SELECTOR.root));

    return roots.map((root) => new SiteNavigation(root, options));
};
